import { gql, useMutation, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import TextArea from '@orn/shared-web/components/Textarea';
import { CheckIcon, CopyIcon, FileIcon, RefreshIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AssistantFeedbackStatus } from '../../__generated__/globalTypes';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';
import { assistantFeedbackAnchorPath, assistantSessionDeepLinkPath } from '../../paths';

import AssistantFeedbackThread from './AssistantFeedbackThread';
import {
  commentCountLabel,
  FEEDBACK_FILTERS,
  filterToStatusVariable,
  formatFeedbackForClipboard,
  transitionActionForStatus,
  type FeedbackFilter,
} from './assistantFeedbackView';

import type {
  AdminListRecentAssistantFeedbackQuery,
  AdminListRecentAssistantFeedbackQueryVariables,
} from './__generated__/AssistantFeedbackScene.gql';
import type {
  AdminResolveAssistantFeedbackMutation,
  AdminResolveAssistantFeedbackMutationVariables,
  AdminReopenAssistantFeedbackMutation,
  AdminReopenAssistantFeedbackMutationVariables,
} from './__generated__/AssistantFeedbackScene.gql';

const ADMIN_LIST_RECENT_FEEDBACK = gql`
  query AdminListRecentAssistantFeedback($before: DateTimeISO, $status: AssistantFeedbackStatus) {
    adminListRecentAssistantFeedback(before: $before, status: $status) {
      hasMore
      nextBefore
      feedback {
        id
        userId
        familyId
        threadId
        turnId
        body
        status
        commentCount
        submitterEmail
        createdAt
      }
    }
  }
`;

const ADMIN_RESOLVE_FEEDBACK = gql`
  mutation AdminResolveAssistantFeedback($id: ID!, $note: String!) {
    adminResolveAssistantFeedback(id: $id, note: $note) {
      id
      status
      commentCount
    }
  }
`;

const ADMIN_REOPEN_FEEDBACK = gql`
  mutation AdminReopenAssistantFeedback($id: ID!, $note: String!) {
    adminReopenAssistantFeedback(id: $id, note: $note) {
      id
      status
      commentCount
    }
  }
`;

type FeedbackRow = AdminListRecentAssistantFeedbackQuery['adminListRecentAssistantFeedback']['feedback'][number];

interface ITransitionModalState {
  row: FeedbackRow;
  action: 'RESOLVE' | 'REOPEN';
}

/**
 * Feedback sub-tab of the Assistant tool. A triage queue over every internal
 * `/issue` submission: filter by status (Open / Resolved / All), expand a row
 * inline to read and add markdown comments, resolve/reopen with a mandatory
 * note, jump to the originating thread transcript, or copy a formatted summary.
 * Paginates older rows via a keyset cursor ("Load more"). Arriving with a
 * `?feedbackId=` anchor scrolls to, highlights, and auto-expands that row.
 */
const AssistantFeedbackScene = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [filter, setFilter] = React.useState<FeedbackFilter>('OPEN');
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [modal, setModal] = React.useState<ITransitionModalState | null>(null);
  const [note, setNote] = React.useState('');

  const { data, loading, fetchMore, refetch } = useQuery<
    AdminListRecentAssistantFeedbackQuery,
    AdminListRecentAssistantFeedbackQueryVariables
  >(ADMIN_LIST_RECENT_FEEDBACK, {
    variables: { before: null, status: filterToStatusVariable(filter) },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const listing = data?.adminListRecentAssistantFeedback;
  const rows = React.useMemo(() => listing?.feedback ?? [], [listing]);
  const hasMore = listing?.hasMore ?? false;
  const nextBefore = listing?.nextBefore ?? null;

  const showStatusColumn = filter === 'ALL';
  const columnCount = showStatusColumn ? 6 : 5;

  // Deep-link anchor (set when arriving from a session turn's feedback badge):
  // scroll the matching row into view, tint it, and auto-expand its thread.
  // Latched so a later "Load more" / re-render doesn't yank the viewport back.
  const [searchParams] = useSearchParams();
  const anchorId = searchParams.get('feedbackId');
  const anchorScrolledRef = React.useRef(false);

  React.useEffect(() => {
    if (anchorId) {
      setExpandedId(anchorId);
    }
  }, [anchorId]);

  React.useEffect(() => {
    if (!anchorId || anchorScrolledRef.current) {
      return;
    }
    document.getElementById(`feedback-row-${anchorId}`)?.scrollIntoView({ block: 'center' });
  }, [anchorId, rows]);

  // Re-settle the scroll after the anchored row's comment thread loads and
  // changes the row height — the initial scroll above fires before that.
  const handleAnchorThreadLoaded = React.useCallback(
    (feedbackId: string) => {
      if (feedbackId !== anchorId || anchorScrolledRef.current) {
        return;
      }
      anchorScrolledRef.current = true;
      document.getElementById(`feedback-row-${anchorId}`)?.scrollIntoView({ block: 'center' });
    },
    [anchorId]
  );

  const [resolveFeedback, { loading: resolving }] = useMutation<
    AdminResolveAssistantFeedbackMutation,
    AdminResolveAssistantFeedbackMutationVariables
  >(ADMIN_RESOLVE_FEEDBACK);
  const [reopenFeedback, { loading: reopening }] = useMutation<
    AdminReopenAssistantFeedbackMutation,
    AdminReopenAssistantFeedbackMutationVariables
  >(ADMIN_REOPEN_FEEDBACK);
  const transitioning = resolving || reopening;

  const changeFilter = (next: FeedbackFilter) => {
    setExpandedId(null);
    setFilter(next);
  };

  const toggleExpanded = (id: string) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  const goToThread = (row: FeedbackRow) => {
    void navigate(
      assistantSessionDeepLinkPath({
        userId: row.userId,
        familyId: row.familyId,
        threadId: row.threadId,
        turnId: row.turnId,
      })
    );
  };

  const copyFeedback = async (row: FeedbackRow) => {
    const text = formatFeedbackForClipboard({
      submitterEmail: row.submitterEmail ?? null,
      userId: row.userId,
      createdAt: row.createdAt,
      body: row.body,
      deeplinkUrl: `${window.location.origin}${assistantFeedbackAnchorPath(row.id)}`,
    });
    await navigator.clipboard.writeText(text);
    showToast({ type: 'success', message: 'Feedback copied' });
  };

  const confirmTransition = async () => {
    if (modal === null) {
      return;
    }
    const trimmed = note.trim();
    if (trimmed.length === 0) {
      return;
    }
    const variables = { id: modal.row.id, note: trimmed };
    if (modal.action === 'RESOLVE') {
      await resolveFeedback({ variables });
    } else {
      await reopenFeedback({ variables });
    }
    setModal(null);
    setNote('');
    setExpandedId(null);
    showToast({ type: 'success', message: modal.action === 'RESOLVE' ? 'Feedback resolved' : 'Feedback reopened' });
    await refetch();
  };

  const handleLoadMore = async () => {
    if (!hasMore || nextBefore === null) {
      return;
    }
    await fetchMore({
      variables: { before: nextBefore, status: filterToStatusVariable(filter) },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          adminListRecentAssistantFeedback: {
            ...fetchMoreResult.adminListRecentAssistantFeedback,
            feedback: [
              ...prev.adminListRecentAssistantFeedback.feedback,
              ...fetchMoreResult.adminListRecentAssistantFeedback.feedback,
            ],
          },
        };
      },
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-fg-secondary text-xs">
          Internal <span className="font-mono">/issue</span> feedback, newest first.
          {rows.length > 0 && <span className="text-fg-tertiary"> · {rows.length} shown</span>}
        </div>
        <div className="flex gap-1">
          {FEEDBACK_FILTERS.map((f) => (
            <Button
              key={f.value}
              color={filter === f.value ? 'primary' : 'secondary'}
              size="xs"
              onClick={() => changeFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="text-fg-secondary text-xs">Loading feedback…</div>
      ) : rows.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">
          {filter === 'OPEN'
            ? 'No open feedback — inbox zero, enjoy it while it lasts.'
            : filter === 'RESOLVED'
              ? 'Nothing resolved yet.'
              : 'No feedback yet — the assistant’s /issue command writes here.'}
        </div>
      ) : (
        <>
          <Table>
            <Table.Header sticky>
              <Table.Row>
                <Table.HeaderCell className="whitespace-nowrap">When</Table.HeaderCell>
                <Table.HeaderCell className="whitespace-nowrap">Submitter</Table.HeaderCell>
                <Table.HeaderCell className="w-full">Feedback</Table.HeaderCell>
                <Table.HeaderCell className="whitespace-nowrap">Comments</Table.HeaderCell>
                {showStatusColumn && <Table.HeaderCell className="whitespace-nowrap">Status</Table.HeaderCell>}
                <Table.HeaderCell className="whitespace-nowrap">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => {
                const action = transitionActionForStatus(row.status);
                const isExpanded = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <Table.Row
                      id={`feedback-row-${row.id}`}
                      onClick={() => toggleExpanded(row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpanded(row.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={isExpanded}
                      className={cx(
                        'cursor-pointer focus-visible:outline focus-visible:outline-2',
                        row.id === anchorId && 'bg-bg-brand-secondary'
                      )}
                    >
                      <Table.Cell
                        mono
                        muted
                        className="whitespace-nowrap"
                        title={new Date(row.createdAt).toISOString()}
                      >
                        {formatTimeAgo(row.createdAt)}
                      </Table.Cell>
                      <Table.Cell mono muted className="whitespace-nowrap" title={row.userId}>
                        {row.submitterEmail ?? row.userId}
                      </Table.Cell>
                      <Table.Cell className="w-full">
                        <span className="line-clamp-2 break-words font-mono text-[11px] leading-snug">{row.body}</span>
                      </Table.Cell>
                      <Table.Cell mono muted className="whitespace-nowrap">
                        {commentCountLabel(row.commentCount ?? null)}
                      </Table.Cell>
                      {showStatusColumn && (
                        <Table.Cell>
                          <StatusBadge status={row.status} />
                        </Table.Cell>
                      )}
                      <Table.Cell>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <IconButton label="Open thread transcript" onClick={() => goToThread(row)}>
                            <FileIcon className="size-4" />
                          </IconButton>
                          <IconButton label="Copy feedback" onClick={() => void copyFeedback(row)}>
                            <CopyIcon className="size-4" />
                          </IconButton>
                          <IconButton
                            label={action.label}
                            onClick={() => {
                              setNote('');
                              setModal({ row, action: action.transition });
                            }}
                          >
                            {action.transition === 'RESOLVE' ? (
                              <CheckIcon className="size-4" />
                            ) : (
                              <RefreshIcon className="size-4" />
                            )}
                          </IconButton>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                    {isExpanded && (
                      <Table.Row className="hover:bg-bg-primary">
                        <Table.Cell colSpan={columnCount} className="bg-bg-secondary p-0">
                          <AssistantFeedbackThread feedbackId={row.id} onCommentsLoaded={handleAnchorThreadLoaded} />
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Body>
          </Table>
          {hasMore && (
            <div className="flex justify-center py-3">
              <Button color="secondary" size="sm" isDisabled={loading} onClick={() => void handleLoadMore()}>
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      <Modal isOpen={modal !== null} onOpenChange={(open) => !open && setModal(null)} size="md">
        <Modal.Header onClose={() => setModal(null)}>
          {modal?.action === 'RESOLVE' ? 'Resolve feedback' : 'Reopen feedback'}
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-secondary text-sm">
              {modal?.action === 'RESOLVE'
                ? 'Describe how this was resolved. The note is appended to the issue’s comments.'
                : 'Explain why this is being reopened. The note is appended to the issue’s comments.'}
            </div>
            {modal && (
              <div className="text-fg-secondary border-border-primary border px-3 py-2">
                <div className="line-clamp-3 font-mono text-[11px] leading-snug">{modal.row.body}</div>
              </div>
            )}
            <TextArea label="Note" placeholder="Markdown supported" value={note} onChange={setNote} rows={3} />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setModal(null)}>
                Cancel
              </Button>
              <Button
                color="primary"
                size="sm"
                isDisabled={note.trim().length === 0 || transitioning}
                isLoading={transitioning}
                onClick={() => void confirmTransition()}
              >
                {modal?.action === 'RESOLVE' ? 'Resolve' : 'Reopen'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

interface IStatusBadgeProps {
  status: AssistantFeedbackStatus;
}

const StatusBadge = ({ status }: IStatusBadgeProps) => (
  <span
    className={cx(
      'border-border-primary rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
      status === AssistantFeedbackStatus.Resolved ? 'text-fg-success-primary' : 'text-fg-warning-primary'
    )}
  >
    {status === AssistantFeedbackStatus.Resolved ? 'Resolved' : 'Open'}
  </span>
);

export default AssistantFeedbackScene;
